import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Minus, Plus, ArrowRight, QrCode, MapPin, Crown, Truck } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ProductPack } from "@/components/ProductPack";
import { ProductCard } from "@/components/ProductCard";
import { UnionJack } from "@/components/UnionJack";
import { useBasket } from "@/context/BasketContext";
import { getProduct, formatPrice, multibuyRate } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

export default function ProductDetail() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [quantity, setQuantity] = useState(1);
    const { addItem } = useBasket();

    useEffect(() => {
        setData(null);
        setNotFound(false);
        setQuantity(1);
        getProduct(slug)
            .then(setData)
            .catch(() => setNotFound(true));
    }, [slug]);

    if (notFound) {
        return (
            <main className="bg-[#F6F1E7] min-h-screen pt-40 pb-24 text-center" data-testid="product-not-found">
                <h1 className="font-serif-display text-4xl">Cut not found.</h1>
                <Link to="/shop" className="btn-luxe btn-green mt-8 inline-flex">Back to Shop</Link>
            </main>
        );
    }

    if (!data) {
        return (
            <main className="bg-[#F6F1E7] min-h-screen pt-32 pb-24">
                <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12">
                    <Skeleton className="h-[480px] rounded-xl bg-black/[0.06]" />
                    <div className="space-y-4 pt-8">
                        <Skeleton className="h-12 w-2/3 bg-black/[0.06]" />
                        <Skeleton className="h-6 w-1/2 bg-black/[0.06]" />
                        <Skeleton className="h-32 w-full bg-black/[0.06]" />
                    </div>
                </div>
            </main>
        );
    }

    const { product, farm, recommended } = data;
    const isPack = product.category === "cut";
    const isAccessory = product.category === "accessory";

    return (
        <main className="bg-[#F6F1E7] min-h-screen pt-24 sm:pt-32 pb-24" data-testid="product-detail-page">
            <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
                <nav className="font-mono-meta text-[10px] tracking-[0.2em] text-black/40 mb-10">
                    <Link to="/shop" className="hover:text-[#0F2A1F] transition-colors" data-testid="breadcrumb-shop-link">SHOP</Link>
                    <span className="mx-2">/</span>
                    <span className="text-black/70">{product.name.toUpperCase()}</span>
                </nav>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
                    {/* Left — imagery */}
                    <Reveal>
                        <div className="bg-gradient-to-b from-[#EDE6D7] to-[#E0D7C4] rounded-xl p-10 sm:p-16 flex items-center justify-center relative overflow-hidden">
                            {product.limited_edition && (
                                <span className="absolute top-5 left-5 font-mono-meta text-[10px] tracking-[0.2em] px-3 py-1.5 bg-[#0B0B0C] text-[#C9B37A]">
                                    LIMITED RELEASE
                                </span>
                            )}
                            {isPack ? (
                                <ProductPack product={product} size="lg" />
                            ) : (
                                <img src={product.images[0]} alt={product.name} className="rounded-lg w-full max-w-md object-cover aspect-[4/5]" />
                            )}
                        </div>
                        {isPack && (
                            <div className="mt-5 rounded-xl overflow-hidden">
                                <img src={product.images[0]} alt={`${product.name} texture`} loading="lazy" className="w-full aspect-[16/7] object-cover" />
                            </div>
                        )}
                    </Reveal>

                    {/* Right — purchase module */}
                    <Reveal delay={0.12}>
                        <p className="eyebrow text-[#8C6A3D] flex items-center gap-2.5">
                            <UnionJack width={16} muted /> Premium British Dried Beef
                        </p>
                        <h1 className="font-serif-display text-4xl sm:text-5xl font-medium text-[#0B0B0C] mt-3" data-testid="product-detail-name">
                            {product.name}
                        </h1>
                        <p className="mt-2 text-black/55 text-sm">{product.subtitle}</p>
                        <p className="font-serif-display text-3xl mt-6 text-[#0F2A1F]" data-testid="product-detail-price">
                            {formatPrice(product.price)}
                        </p>

                        {!isAccessory && (
                            <>
                                <div className="grid grid-cols-3 gap-px bg-black/10 border border-black/10 rounded-lg overflow-hidden mt-8">
                                    {[
                                        ["PROTEIN", `${product.protein_g}g`],
                                        ["WEIGHT", `${product.weight_g}g`],
                                        ["INGREDIENTS", product.ingredients.length],
                                    ].map(([label, value]) => (
                                        <div key={label} className="bg-[#F6F1E7] p-4 text-center">
                                            <p className="font-mono-meta text-[9px] tracking-[0.2em] text-black/40">{label}</p>
                                            <p className="font-serif-display text-2xl font-medium mt-1">{value}</p>
                                        </div>
                                    ))}
                                </div>

                                <p className="font-mono-meta text-[10px] tracking-wide text-black/45 mt-4">
                                    {product.ingredients.join(" · ").toUpperCase()} — NOTHING ELSE
                                </p>
                            </>
                        )}

                        <div className="flex items-center gap-4 mt-8">
                            <div className="flex items-center border border-black/15 rounded-sm">
                                <button
                                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                                    className="w-11 h-12 flex items-center justify-center hover:bg-black/5 transition-colors"
                                    aria-label="Decrease quantity"
                                    data-testid="product-quantity-decrease"
                                >
                                    <Minus size={14} />
                                </button>
                                <span className="w-10 text-center text-sm font-semibold" data-testid="product-quantity-value">{quantity}</span>
                                <button
                                    onClick={() => setQuantity((q) => Math.min(50, q + 1))}
                                    className="w-11 h-12 flex items-center justify-center hover:bg-black/5 transition-colors"
                                    aria-label="Increase quantity"
                                    data-testid="product-quantity-increase"
                                >
                                    <Plus size={14} />
                                </button>
                            </div>
                            <button
                                onClick={() => addItem(product, quantity)}
                                className="btn-luxe btn-green flex-1"
                                data-testid="product-detail-add-to-basket"
                            >
                                Add to Basket — {formatPrice(product.price * quantity * (1 - multibuyRate(product.category, quantity)))}
                            </button>
                        </div>

                        {/* Multi-buy incentive — cuts only */}
                        {isPack && (
                            <div className="mt-5" data-testid="multibuy-options">
                                <div className="grid grid-cols-3 gap-2">
                                    {[
                                        { qty: 1, label: "Single", sub: "Full price" },
                                        { qty: 3, label: "3 Packs", sub: "Save 5%" },
                                        { qty: 6, label: "6 Packs", sub: "Save 10%" },
                                    ].map((opt) => (
                                        <button
                                            key={opt.qty}
                                            onClick={() => setQuantity(opt.qty)}
                                            className={`border rounded-sm px-3 py-3 text-center transition-colors duration-200 ${
                                                quantity === opt.qty
                                                    ? "border-[#0F2A1F] bg-[#0F2A1F] text-[#F6F1E7]"
                                                    : "border-black/15 hover:border-[#0F2A1F]"
                                            }`}
                                            data-testid={`multibuy-option-${opt.qty}`}
                                        >
                                            <span className="block text-sm font-semibold">{opt.label}</span>
                                            <span className={`block font-mono-meta text-[9px] tracking-[0.15em] mt-1 ${
                                                quantity === opt.qty ? "text-[#C9B37A]" : opt.qty > 1 ? "text-[#8C6A3D]" : "text-black/40"
                                            }`}>
                                                {opt.sub.toUpperCase()}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                                {multibuyRate(product.category, quantity) > 0 && (
                                    <p className="font-mono-meta text-[10px] tracking-wide text-[#0F2A1F] mt-3" data-testid="multibuy-saving-note">
                                        YOU SAVE {formatPrice(product.price * quantity * multibuyRate(product.category, quantity))} WITH MULTI-BUY
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Free delivery nudge */}
                        <p className="flex items-center gap-2 font-mono-meta text-[10px] tracking-wide text-black/45 mt-4">
                            <Truck size={13} className="text-[#0F2A1F]" />
                            COMPLIMENTARY DELIVERY ON ORDERS OVER £40
                        </p>

                        {/* Club member upsell */}
                        <Link
                            to="/club"
                            className="group block mt-6 border border-[#C9B37A]/50 bg-[#0F2A1F] rounded-xl p-5 hover:border-[#C9B37A] transition-colors duration-300"
                            data-testid="pdp-club-upsell"
                        >
                            <div className="flex items-center gap-4">
                                <Crown size={24} strokeWidth={1.2} className="text-[#C9B37A] flex-shrink-0" />
                                <div className="flex-1">
                                    <p className="font-serif-display text-lg font-medium text-[#F6F1E7]">
                                        Members save up to 20% on every order
                                    </p>
                                    <p className="font-mono-meta text-[9px] tracking-[0.18em] text-[#C9B37A] mt-1.5">
                                        THE BRITISH BEEF CLUB · FROM £33/MONTH · MONTHLY DELIVERIES INCLUDED
                                    </p>
                                </div>
                                <ArrowRight size={16} className="text-[#C9B37A] group-hover:translate-x-1 transition-transform" />
                            </div>
                        </Link>

                        <div className="mt-10 space-y-5 text-sm text-black/65 leading-relaxed">
                            <p>{product.description}</p>
                            <p className="font-serif-display italic text-lg text-black/75">“{product.tasting_notes}”</p>
                        </div>

                        {/* Traceability */}
                        <div className="mt-10 border border-black/10 rounded-xl p-6 bg-white/40" data-testid="product-traceability">
                            <div className="flex items-start justify-between gap-6">
                                <div>
                                    <p className="eyebrow text-[#8C6A3D] mb-2">Full Traceability</p>
                                    <p className="font-mono-meta text-xs text-black/70" data-testid="product-batch-number">
                                        BATCH {product.batch_number}
                                    </p>
                                    {farm ? (
                                        <>
                                            <p className="text-sm text-black/60 mt-3 flex items-center gap-1.5">
                                                <MapPin size={13} className="text-[#0F2A1F]" />
                                                {farm.name}, {farm.location_name}
                                            </p>
                                            <p className="text-xs text-black/45 mt-1">
                                                {farm.breed} · Raised by {farm.farmer_name}
                                            </p>
                                            <Link
                                                to={`/farms/${farm.id}`}
                                                className="inline-flex items-center gap-1.5 eyebrow text-[#0F2A1F] hover:text-[#8C6A3D] transition-colors mt-4"
                                                data-testid="product-farm-link"
                                            >
                                                Trace this batch <ArrowRight size={12} />
                                            </Link>
                                        </>
                                    ) : isAccessory ? (
                                        <p className="text-sm text-black/60 mt-3">
                                            Designed by British Steak Co. and crafted by trusted British makers.
                                        </p>
                                    ) : (
                                        <p className="text-sm text-black/60 mt-3">
                                            A collection drawn from all five of our partner farms.
                                        </p>
                                    )}
                                </div>
                                <div className="flex flex-col items-center gap-2 flex-shrink-0">
                                    <div className="w-16 h-16 border border-black/15 rounded-sm p-1.5 bg-white">
                                        <QrCode className="w-full h-full text-black/75" strokeWidth={1} />
                                    </div>
                                    <span className="font-mono-meta text-[8px] tracking-[0.15em] text-black/40">SCAN THE PACK</span>
                                </div>
                            </div>
                            {!isAccessory && (
                                <p className="font-mono-meta text-[9px] tracking-[0.15em] text-black/45 leading-relaxed border-t border-black/10 mt-5 pt-4" data-testid="product-provenance-note">
                                    100% BRITISH BEEF · RAISED ON NAMED BRITISH FARMS · TRADITIONALLY PROCESSED — NEVER HALAL
                                </p>
                            )}
                        </div>
                    </Reveal>
                </div>

                {/* Recommended */}
                {recommended?.length > 0 && (
                    <div className="mt-24">
                        <Reveal className="text-center mb-10">
                            <h2 className="font-serif-display text-3xl sm:text-4xl font-medium">You may also like</h2>
                        </Reveal>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                            {recommended.map((p) => (
                                <ProductCard key={p.id} product={p} />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </main>
    );
}
